/**
 * Product changelog shown to players via the header "Changelog" button.
 *
 * This is a hand-authored, Russian-language log of user-visible changes. It is
 * intentionally separate from `packages/cards/src/content/card-releases.ts`,
 * which is a machine journal of per-card content changes (card ids + content
 * version) with no prose. Here we describe features, fixes, balance and UI
 * changes in words a player understands.
 *
 * How to maintain: keep one entry per production release date at the TOP of
 * `RELEASE_NOTES` (newest first). Versions are date-based (`YYYY.MM.DD`), so a
 * second release on the same date updates and condenses the existing entry
 * instead of creating a duplicate. Text is written in Russian, for players.
 * The `release-production` skill fills this in as part of shipping a release.
 */

export type ReleaseNoteKind = 'feature' | 'fix' | 'balance' | 'ui';

export interface ReleaseNoteHighlight {
  /** Category of the change; drives the label shown next to the line. */
  readonly kind: ReleaseNoteKind;
  /** One user-facing sentence in Russian. */
  readonly text: string;
}

export interface ReleaseNote {
  /** Date-based version, e.g. `2026.07.18`. Matches the release date. */
  readonly version: string;
  /** ISO date `YYYY-MM-DD` of the release. */
  readonly date: string;
  /** Optional short headline summarising the release. */
  readonly title?: string;
  /** Ordered list of user-visible changes in this release. */
  readonly highlights: readonly ReleaseNoteHighlight[];
}

/** Human label per change kind, shown as a small tag on each line. */
export const RELEASE_NOTE_KIND_LABELS: Readonly<Record<ReleaseNoteKind, string>> = {
  feature: 'Новое',
  fix: 'Фикс',
  balance: 'Баланс',
  ui: 'Интерфейс',
};

/** Newest release first. */
export const RELEASE_NOTES: readonly ReleaseNote[] = [
  {
    version: '2026.10.05',
    date: '2026-10-05',
    title: 'Перегрев, Заморозка и новый облик',
    highlights: [
      { kind: 'feature', text: 'Перегрев и Заморозка стали отдельными статусами: при Перегреве 4 карта расплавляется, а Заморозка пропускает ближайшие бои.' },
      { kind: 'feature', text: 'Новые карты: Нестабильный реактор, Ядро расплава и Термальный инвертор; карты на Перегрев переработаны и усилены.' },
      { kind: 'balance', text: 'С 6-го раунда в начале раунда добираются две карты, а за каждую карту, которой не хватает в колоде, игрок получает 1 урон усталости.' },
      { kind: 'balance', text: 'Колониальный хищник получает +4 к атаке, пока ваша сторона его локации заполнена любыми картами.' },
      { kind: 'ui', text: 'Новый облик «Аркадный автомат»: ночной стол, пиксельные шрифты и тёмные карты со светлым окном арта на всех экранах.' },
      { kind: 'ui', text: 'Бой стал зрелищнее: удары с искрами, гибель и раскрытие карт, а показ хода можно ускорить вдвое или пропустить.' },
      { kind: 'ui', text: 'На телефоне в альбомной ориентации поле занимает всю высоту, а рука стоит справа.' },
      { kind: 'fix', text: 'Ники игроков в бою больше не обрезаются, а фон не дёргается при загрузке страницы.' },
    ],
  },
  {
    version: '2026.07.30',
    date: '2026-07-30',
    title: 'Новые стратегии и ясный редактор колод',
    highlights: [
      { kind: 'feature', text: 'В случайный выбор соперника добавлены средневековая колода с «Запасом энергии» и мебельная защитная колода с «Вооружёнными структурами».' },
      { kind: 'balance', text: 'Звёздный цветок теперь тратит 4 Роста и усиливает все ваши карты в своей локации на +2/+2.' },
      { kind: 'fix', text: 'Эхо развёртывания больше не занимает ячейку, куда уже подготовлена ваша карта.' },
      { kind: 'ui', text: 'В редакторе колод на телефоне каталог стал компактнее, а полное описание карты или пассивки открывается перед выбором.' },
      { kind: 'ui', text: 'Постоянные усиления и лечение при появлении карт теперь показываются направленными анимациями и заметным приростом характеристик.' },
    ],
  },
  {
    version: '2026.07.29',
    date: '2026-07-29',
    title: 'Удобнее мобильный бой и подбор колод',
    highlights: [
      { kind: 'ui', text: 'На телефонах локации теперь располагаются одна под другой, а поле, рука и элементы управления занимают экран устойчивее, в том числе во время воспроизведения раунда.' },
      { kind: 'ui', text: 'Карты на поле получили более выразительное полноразмерное оформление, а взятые и созданные карты появляются плавной анимацией.' },
      { kind: 'fix', text: 'Перетаскивание на телефонах, прокрутка карт, кнопка готовности и отображение характеристик работают надёжнее, а атакующие анимации больше не обрезаются.' },
      { kind: 'ui', text: 'Колода выбирается до входа в матч, без повторного выбора в игровой комнате.' },
      { kind: 'feature', text: 'Помощник в редакторе стал лучше подбирать составы колод и подходящие пассивки.' },
      { kind: 'ui', text: 'Перед применением предложения помощника теперь видно состав, кривую стоимости и изменения относительно текущей колоды.' },
      { kind: 'fix', text: 'Предложение помощника нельзя применить поверх изменённого черновика, а применённый состав можно вернуть одним действием.' },
      { kind: 'fix', text: 'Рекомендованные помощником карты сразу отображаются в каталоге редактора.' },
    ],
  },
  {
    version: '2026.07.28',
    date: '2026-07-28',
    title: 'Яснее бой, челленджи и обучение',
    highlights: [
      { kind: 'fix', text: 'Соперники в челленджах больше не тратят карты на заведомо занятые ячейки поля.' },
      { kind: 'ui', text: 'Рост и превращение растений теперь показываются в правильном порядке, а трата Роста не выглядит как полученный урон.' },
      { kind: 'feature', text: 'Карты можно подготовить в одну ячейку: они раскроются по очереди, а карта без свободного места уйдёт в сброс.' },
      { kind: 'balance', text: 'Ослепляющая лампа избегает подходящей атаки без урона, Сундук с барахлом берёт карту сразу после гибели, а Стул на четырёх ножках прекращает удары при собственной гибели.' },
      { kind: 'balance', text: 'Пассивка «Эхо развёртывания» срабатывает сразу после появления подходящей карты.' },
      { kind: 'ui', text: 'Челленджи теперь показывают путь из боёв к награде на одной наглядной шкале.' },
      { kind: 'ui', text: 'Обучение стало короче и понятнее; его можно пропустить с подтверждением и вернуться к нему позже в челленджах.' },
      { kind: 'ui', text: 'Обновлены верхнее меню, значки челленджей и песочницы, а внизу экрана появилась строка статуса бета-версии.' },
      { kind: 'ui', text: 'Переходы по разделам, просмотр колоды и анимации раскрытия карт стали плавнее и понятнее.' },
      { kind: 'fix', text: 'На мобильных устройствах характеристики карт больше не переносятся на название, а лобби сохраняет устойчивую разметку во время поиска матча.' },
    ],
  },
  {
    version: '2026.07.27',
    date: '2026-07-27',
    title: 'Новая очередь раскрытия и обновление карт',
    highlights: [
      { kind: 'feature', text: 'Карты на поле, заклинания и вирусы теперь раскрываются в единой очереди: каждая карта полностью разрешается перед следующей.' },
      { kind: 'balance', text: 'Обновлены механики и баланс взрослых Деревьев, слизней, роботов, мебели и средневековых карт.' },
      { kind: 'feature', text: 'Механика и карты «Сигнала» выведены из игры.' },
      { kind: 'fix', text: 'Последствия каждого размена в бою теперь разрешаются до следующей линии, поэтому усиления и гибели могут влиять на её исход.' },
      { kind: 'ui', text: 'Взятые карты прилетают из колоды в руку, а Поглощение и гибель карт показываются яснее.' },
      { kind: 'fix', text: 'Улучшены выбор целей, реакции на смертельный урон, мобильное прицеливание и превью заклинаний.' },
      { kind: 'balance', text: 'Соперники в челленджах получили доработанные составы колод.' },
    ],
  },
  {
    version: '2026.07.23',
    date: '2026-07-23',
    title: 'Умные рекомендации колод и новые правила боя',
    highlights: [
      { kind: 'feature', text: 'Помощник в редакторе колод теперь предлагает полный состав вместе с подходящей пассивкой.' },
      { kind: 'balance', text: 'Барьер теперь постоянно защищает союзные карты в своей локации, уменьшая входящий урон на 2.' },
      { kind: 'fix', text: 'Ослепляющая лампа после перемещения корректно меняет цели ещё не начавшихся атак.' },
      { kind: 'ui', text: 'Повторяющиеся эффекты на картах теперь объединяются в один понятный статус.' },
      { kind: 'fix', text: 'Песочница надёжнее открывается при повторной инициализации страницы.' },
    ],
  },
  {
    version: '2026.07.22',
    date: '2026-07-22',
    title: 'Новые правила карт, быстрое обучение и колоды челленджей',
    highlights: [
      { kind: 'feature', text: 'Новые игроки автоматически получают «Учебный отряд», а первый матч теперь начинается с наглядного разбора карты.' },
      { kind: 'feature', text: 'Колоду пройденного боя челленджа можно сохранить в библиотеку и открыть в редакторе.' },
      { kind: 'balance', text: 'Переработаны механики и баланс Роя, слизней, Растений, Перегрева, мебели и средневековых карт.' },
      { kind: 'balance', text: 'Соперники в челленджах получили более разнообразные и заново сбалансированные колоды и пассивки.' },
      { kind: 'fix', text: 'Перемещение и перехват во время боя теперь корректно влияют только на ещё не начавшиеся атаки.' },
      { kind: 'feature', text: 'В песочнице подготовленные к раскрытию карты обеих сторон можно переставлять между ячейками или возвращать с поля.' },
      { kind: 'fix', text: 'Ослепляющая лампа теперь перемещается в свободную союзную ячейку и гарантированно избегает первой подходящей атаки в раунде.' },
    ],
  },
  {
    version: '2026.07.21',
    date: '2026-07-21',
    title: 'Челленджи колод и удобная мобильная песочница',
    highlights: [
      { kind: 'feature', text: 'Добавлены пять челленджей колод с последовательными боями, сохраняемым прогрессом и неограниченными попытками.' },
      { kind: 'feature', text: 'За прохождение челленджей открываются Вольтов накопитель, Сборщик утиля, Королевское желе, Заводной пуф и Звёздный цветок.' },
      { kind: 'balance', text: 'Наградные карты усиливают Перегрев, токены, слизней, заполненные локации и Рост.' },
      { kind: 'fix', text: 'Открытые наградные карты корректно появляются в каталоге, редакторе и предложениях помощника; закрытые награды помощник больше не предлагает.' },
      { kind: 'fix', text: 'Челленджи сразу используют подготовленные колоды, завершённый бой можно повторить, а после входа через Google каталог открывается корректно.' },
      { kind: 'ui', text: 'Обучение в лобби теперь рекомендует челленджи как способ освоить игровые механики и открыть новые карты.' },
      { kind: 'fix', text: 'Перетаскивание пассивок в Safari работает корректно, а мобильный стол песочницы больше не создаёт лишнюю прокрутку.' },
      { kind: 'ui', text: 'Перемещение пассивок и открытие подробностей карт стали плавнее, сохранив чёткий пиксельный вид.' },
    ],
  },
  {
    version: '2026.07.20',
    date: '2026-07-20',
    title: 'Пассивки, новый бой и персональная песочница',
    highlights: [
      { kind: 'feature', text: 'Добавлены пять пассивок колод: Регенерация, Нарастающий урон, Вооружённые постройки, Запас энергии и Эхо развёртывания.' },
      { kind: 'feature', text: 'В персональной песочнице теперь можно сохранять сценарии, редактировать поле и запускать полноценный бой.' },
      { kind: 'feature', text: 'Помощник научился анализировать ситуацию в песочнице и предлагать изменения перед их применением.' },
      { kind: 'balance', text: 'Стартовое здоровье игроков увеличено с 20 до 30.' },
      { kind: 'balance', text: 'Колючки теперь складывают урон, а Пожирание запускает способности, срабатывающие при гибели.' },
      { kind: 'ui', text: 'Улучшены раскрытие, перемещение и внешний вид карт, а пассивки получили собственное оформление и анимации.' },
      { kind: 'fix', text: 'Устранены повторное появление уже разыгранных карт и проблемы с сохранением песочницы.' },
      { kind: 'fix', text: 'Открытая во время обновления игра теперь автоматически загружает новую версию.' },
    ],
  },
  {
    version: '2026.07.19',
    date: '2026-07-19',
    title: 'Встречный бой, порядок раскрытия и надёжный вход',
    highlights: [
      { kind: 'feature', text: 'Стоящие друг напротив друга бойцы теперь атакуют одновременно, а нанесённый ими урон показывается вместе.' },
      { kind: 'ui', text: 'Карты раскрываются в порядке выкладки; повторно выложенная карта раскрывается последней.' },
      { kind: 'ui', text: 'Статусы и характеристики карт получили более компактные пиксельные иконки.' },
      { kind: 'fix', text: 'Исправлен вход через Google на iPhone и iPad.' },
      { kind: 'fix', text: 'Боевое перемещение карт теперь корректно завершается перед встречной атакой.' },
      { kind: 'fix', text: 'При серьёзной ошибке вместо пустого экрана появляется сообщение с предложением обновить страницу.' },
    ],
  },
  {
    version: '2026.07.18',
    date: '2026-07-18',
    title: 'Растения, колоды и боевые анимации',
    highlights: [
      { kind: 'feature', text: 'Добавлено 15 играбельных карт-растений, взрослые формы деревьев и новая ботовская колода растений.' },
      { kind: 'feature', text: 'Составы колод теперь можно копировать и вставлять.' },
      { kind: 'feature', text: 'Помощник научился работать с вашими сохранёнными колодами.' },
      { kind: 'feature', text: 'Переходы между главной страницей, поиском игры, колодами и каталогом стали быстрее и плавнее.' },
      { kind: 'balance', text: 'Крона теперь работает только у саженцев и переносит весь урон одной атаки на зарезервированный Лист.' },
      { kind: 'balance', text: 'Дуб, баньян и тёрн созревают быстрее, а их взрослые формы стали сильнее.' },
      { kind: 'balance', text: 'Великий баньян теперь создаёт Лист после своей первой атаки в раунде.' },
      { kind: 'ui', text: 'Улучшены анимации атак, перемещения, разыгрывания и гибели карт.' },
      { kind: 'ui', text: 'Обновлены окна с подробностями карт, действия колод и пиксельные иконки.' },
      { kind: 'fix', text: 'Исправлены порядок отображения урона, мерцание окон и другие визуальные ошибки во время боя.' },
      { kind: 'fix', text: 'Улучшена работа редактора колод и управления картами на мобильных устройствах.' },
    ],
  },
  {
    version: '2026.07.17',
    date: '2026-07-17',
    title: 'Средневековые карты и игра прямо на поле',
    highlights: [
      { kind: 'feature', text: 'Добавлено 15 универсальных средневековых карт: юниты, лечение, добор карт, усиления, ослабления и жетоны ополчения.' },
      { kind: 'feature', text: 'Карты без цели теперь можно разыгрывать, перетаскивая их прямо на поле.' },
      { kind: 'feature', text: 'Карты в руке теперь можно переставлять мышью или касанием.' },
      { kind: 'balance', text: 'Лечение теперь может поднимать здоровье выше стартового значения.' },
      { kind: 'ui', text: 'Перемещение и возврат разыгранных карт стали плавнее.' },
      { kind: 'fix', text: 'Щитоносца снова можно корректно разыграть, последовательно выбрав место и союзную цель.' },
      { kind: 'fix', text: 'Исправлены зависания и визуальные скачки при перетаскивании карт.' },
    ],
  },
];
